import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback } from 'react'

export interface BaiduMapProps {
    center: { lat: number; lng: number }
    zoom?: number
    style?: React.CSSProperties
    className?: string
    options?: {
        zoomControl?: boolean
        streetViewControl?: boolean
        fullscreenControl?: boolean
        mapTypeControl?: boolean
        scrollWheelZoom?: boolean
    }
    onLoad?: (map: any) => void
    onZoomChanged?: () => void
    onCenterChanged?: () => void
    onIdle?: () => void
    children?: React.ReactNode
}

export interface BaiduMapRef {
    getMap: () => any
}

const BaiduMapContext = React.createContext<any>(null)

export const useBaiduMap = () => React.useContext(BaiduMapContext)

const BaiduMap = forwardRef<BaiduMapRef, BaiduMapProps>(({
    center,
    zoom = 15,
    style,
    className,
    options,
    onLoad,
    onZoomChanged,
    onCenterChanged,
    onIdle,
    children
}, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const mapRef = useRef<any>(null)
    const [mapReady, setMapReady] = useState(false)
    const isInitialized = useRef(false)
    const onLoadRef = useRef(onLoad)
    const onZoomChangedRef = useRef(onZoomChanged)
    const onCenterChangedRef = useRef(onCenterChanged)
    const onIdleRef = useRef(onIdle)

    useEffect(() => {
        onLoadRef.current = onLoad
        onZoomChangedRef.current = onZoomChanged
        onCenterChangedRef.current = onCenterChanged
        onIdleRef.current = onIdle
    }, [onLoad, onZoomChanged, onCenterChanged, onIdle])

    useImperativeHandle(ref, () => ({
        getMap: () => mapRef.current
    }))

    const handleZoomChanged = useCallback(() => {
        onZoomChangedRef.current?.()
    }, [])

    const handleCenterChanged = useCallback(() => {
        onCenterChangedRef.current?.()
    }, [])

    const handleIdle = useCallback(() => {
        onIdleRef.current?.()
    }, [])

    useEffect(() => {
        if (!containerRef.current || !window.BMapGL || isInitialized.current) return

        const map = new window.BMapGL.Map(containerRef.current, {
            enableMapClick: true,
        })

        const point = new window.BMapGL.Point(center.lng, center.lat)
        map.centerAndZoom(point, zoom)
        if (options?.scrollWheelZoom === false) {
            map.disableScrollWheelZoom()
        } else {
            map.enableScrollWheelZoom(true)
        }

        // 컨트롤 추가
        if (options?.zoomControl !== false) {
            map.addControl(new window.BMapGL.ZoomControl())
        }

        mapRef.current = map
        isInitialized.current = true
        setMapReady(true)
        onLoadRef.current?.(map)

        map.addEventListener('zoomend', handleZoomChanged)
        map.addEventListener('moveend', handleCenterChanged)
        map.addEventListener('tilesloaded', handleIdle)

        // SDK의 첫 tilesloaded 이벤트보다 React의 onLoad 상태 반영이 늦더라도
        // 대시보드 bbox를 반드시 한 번 계산할 수 있게 현재 콜백을 다시 호출한다.
        const initialIdleTimer = window.setTimeout(handleIdle, 100)

        return () => {
            window.clearTimeout(initialIdleTimer)
            map.removeEventListener('zoomend', handleZoomChanged)
            map.removeEventListener('moveend', handleCenterChanged)
            map.removeEventListener('tilesloaded', handleIdle)
            map.clearOverlays?.()
            containerRef.current?.replaceChildren()
            mapRef.current = null
            isInitialized.current = false
        }
    }, [])

    // center 변경 시 panTo
    useEffect(() => {
        if (!mapRef.current || !isInitialized.current) return
        const point = new window.BMapGL.Point(center.lng, center.lat)
        mapRef.current.panTo(point)
    }, [center.lat, center.lng])

    return (
        <div className={className} style={{ width: '100%', height: '100%', ...style }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
            {mapReady && mapRef.current && (
                <BaiduMapContext.Provider value={mapRef.current}>
                    {children}
                </BaiduMapContext.Provider>
            )}
        </div>
    )
})

export default BaiduMap
export { BaiduMapContext }
